import { FC } from "react";

import { Stack, Shelf, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";
import { Panel, Tag, Tooltip } from "capitalrx-components-2.0/lib/general";
import { formatDollars } from "capitalrx-utilities";

import { RESPONSIVE_LAYOUT } from "./BenefitSearchForm";
import styles from "./BenefitSearchForm.module.scss";
import { MedicalBenefitResponse } from "@/api/interfaces/Medical";

interface ResultPanelProps {
  results: MedicalBenefitResponse | null;
  diagnosis_codes: string[] | null;
}

export const ResultPanel: FC<ResultPanelProps> = ({ results, diagnosis_codes }) => {
  const { Layout } = useResponsive(RESPONSIVE_LAYOUT);
  const displayMode = useResponsiveDisplayMode();

  return (
    <Stack>
      <Panel>
        {results?.result.status !== "denied" && (
          <Stack spacing="medium-large">
            <Stack spacing="small" alignment="left">
              <Layout spacing={displayMode === "mobile" ? "small" : "medium"}>
                <Subheading>Procedure Code {results?.result.procedure_code}</Subheading>
                <Shelf spacing="mini">
                  <Tag type="info-high-contrast">{results?.result.service_category}</Tag>
                  <Tag type="success-high-contrast">Covered</Tag>
                  <Shelf.Spacer />
                </Shelf>
                <Shelf.Spacer />
              </Layout>
              <Shelf>
                <Tooltip alignment="top" content="Diagnosis Code">{(ref) => <div ref={ref}><Body size="small">{diagnosis_codes}</Body></div>}</Tooltip>
                <Tooltip alignment="top" content="Place of Service">{(ref) => <div ref={ref}><Body size="small">{results?.result.place_of_service}</Body></div>}</Tooltip>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
            <Stack spacing="small">
              <Body className={`${styles.SectionSpacer} ${styles.resultText}`}>
                in-network provider
              </Body>
              <Stack spacing="mini">
                <Semibold>Member Cost Share</Semibold><Body>{results?.result.total_patient_pay_amount !== null ? formatDollars(results?.result.total_patient_pay_amount) : "N/A"}</Body>
              </Stack>
            </Stack>
            <Stack spacing="small">
              <Body className={`${styles.SectionSpacer} ${styles.resultText}`}>
                out-of-network provider
              </Body>
              <Stack spacing="mini">
                <Semibold>Member Cost Share</Semibold><Body>{results?.result.out_of_network_repricing !== null ? formatDollars(results?.result.out_of_network_repricing) : "N/A"}</Body>
              </Stack>
            </Stack>
          </Stack>
        )}
        {results?.result.status === "denied" && (
          <Stack spacing="medium-large">
            <Stack>
              <Shelf>
                <Subheading>Procedure Code {results?.result.procedure_code}</Subheading>
                <Tag type="info-high-contrast">{results?.result.service_category}</Tag>
                <Tag type="error-high-contrast">Not Covered</Tag>
                <Shelf.Spacer />
              </Shelf>
              <Shelf>
                <Tooltip alignment="top" content="Diagnosis Code(s)">{(ref) => <div ref={ref}><Body size="small">{diagnosis_codes}</Body></div>}</Tooltip>
                <Tooltip alignment="top" content="Place of Service">{(ref) => <div ref={ref}><Body size="small">{results?.result.place_of_service}</Body></div>}</Tooltip>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
            <Layout>
              <Semibold>Reason</Semibold>
              <Body>
                {results?.result.reasons_for_denial.map((reason, index) => (
                  <span key={index}>
                    {reason}.
                    {index < results.result.reasons_for_denial.length - 1 ? " " : ""}
                  </span>
                ))}
              </Body>
            </Layout>
          </Stack>
        )}

      </Panel>
    </Stack>
  );
};
